// src/pages/Communication.jsx
import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Snackbar,
  CircularProgress,
  Divider,
  Paper,
  Stack,
  InputAdornment,
  Chip
} from '@mui/material';
import {
  Send as SendIcon,
  Group as GroupIcon,
  Email as EmailIcon,
  Link as LinkIcon,
  Title as TitleIcon,
  Description as DescriptionIcon,
  CheckCircle as CheckCircleIcon,
  People as PeopleIcon
} from '@mui/icons-material';
import { db } from '../firebase/config';
import { collection, getDocs } from 'firebase/firestore';

const Communication = ({ onSuccess }) => {
  const [loading, setLoading] = useState(false);
  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [selectedYearGroup, setSelectedYearGroup] = useState('all');
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [messageType, setMessageType] = useState('general');
  const [yearGroups, setYearGroups] = useState(new Map());
  const [availableYearGroups, setAvailableYearGroups] = useState([]);
  const [notificationData, setNotificationData] = useState({
    type: 'general_announcement',
    title: '',
    content: '',
    buttonText: '',
    buttonLink: '',
    recipients: []
  });
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });

  // Helper function to get year group prefix
  const getYearGroupPrefix = (yearName) => {
    if (!yearName || yearName.trim() === '') return 'not';
    const prefix = yearName.substring(0, 3).toLowerCase();
    return prefix === '' ? 'not' : prefix;
  };

  // Fetch all users on component mount
  useEffect(() => {
    fetchUsers();
  }, []);

  // Filter users when selected year group changes
  useEffect(() => {
    filterUsersByYearGroup();
  }, [selectedYearGroup, users]);

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const usersRef = collection(db, 'users');
      const snapshot = await getDocs(usersRef);
      const usersList = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      setUsers(usersList);
      
      // Group users by year group prefix
      const groups = new Map();
      
      usersList.forEach(user => {
        const yearName = user.yearName || 'Not specified';
        const prefix = getYearGroupPrefix(yearName);
        
        const userData = {
          id: user.id,
          name: user.fullName || user.firstName || user.email,
          email: user.email,
          yearName: yearName,
          phone: user.phone
        };
        
        if (!groups.has(prefix)) {
          groups.set(prefix, {
            displayName: yearName,
            prefix: prefix,
            members: [],
            count: 0
          });
        }
        
        const group = groups.get(prefix);
        group.members.push(userData);
        group.count++;
      });
      
      setYearGroups(groups);
      
      // Convert Map to array for dropdown options
      const groupsArray = Array.from(groups.entries()).map(([prefix, group]) => ({
        prefix,
        displayName: group.displayName,
        count: group.count
      }));
      
      // Sort groups by display name
      groupsArray.sort((a, b) => a.displayName.localeCompare(b.displayName));
      setAvailableYearGroups(groupsArray);
      
      // Auto-select all users initially
      setFilteredUsers(usersList);
      setSelectedUsers(usersList.map(user => user.id));
      
      console.log(`Fetched ${usersList.length} users`);
      console.log(`Year groups:`, groupsArray);
    } catch (error) {
      console.error('Error fetching users:', error);
      showSnackbar('Failed to fetch users', 'error');
    } finally {
      setLoading(false);
    }
  };

  const filterUsersByYearGroup = () => {
    if (selectedYearGroup === 'all') {
      setFilteredUsers(users);
      setSelectedUsers(users.map(user => user.id));
    } else {
      const group = yearGroups.get(selectedYearGroup);
      if (group) {
        const filtered = group.members.map(member => {
          return users.find(user => user.id === member.id);
        }).filter(Boolean);
        
        setFilteredUsers(filtered);
        setSelectedUsers(filtered.map(user => user.id));
      } else {
        setFilteredUsers([]);
        setSelectedUsers([]);
      }
    }
  };

  const handleYearGroupChange = (event) => {
    const prefix = event.target.value;
    setSelectedYearGroup(prefix);
  };

  const handleSelectAll = () => {
    if (selectedUsers.length === filteredUsers.length) {
      setSelectedUsers([]);
    } else {
      setSelectedUsers(filteredUsers.map(user => user.id));
    }
  };

  const handleUserToggle = (userId) => {
    if (selectedUsers.includes(userId)) {
      setSelectedUsers(selectedUsers.filter(id => id !== userId));
    } else {
      setSelectedUsers([...selectedUsers, userId]);
    }
  };

  const handleInputChange = (field, value) => {
    setNotificationData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleMessageTypeChange = (type) => {
    setMessageType(type);
    if (type === 'meeting') {
      setNotificationData(prev => ({
        ...prev,
        type: 'meeting_created',
        buttonText: 'Join Meeting',
        buttonLink: ''
      }));
    } else {
      setNotificationData(prev => ({
        ...prev,
        type: 'general_announcement',
        buttonText: '',
        buttonLink: ''
      }));
    }
  };

  const validateForm = () => {
    if (!notificationData.title.trim()) {
      showSnackbar('Please enter a title', 'error');
      return false;
    }
    if (!notificationData.content.trim()) {
      showSnackbar('Please enter message content', 'error');
      return false;
    }
    if (selectedUsers.length === 0) {
      showSnackbar('Please select at least one recipient', 'error');
      return false;
    }
    if (messageType === 'meeting' && !notificationData.buttonLink) {
      showSnackbar('Please enter a meeting link', 'error');
      return false;
    }
    return true;
  };

  const sendNotifications = async () => {
    if (!validateForm()) return;

    setLoading(true);
    try {
        // Prepare the notification payload (emails only)
        const notificationPayload = {
            type: notificationData.type,
            title: notificationData.title,
            content: notificationData.content,
            buttonText: notificationData.buttonText,
            buttonLink: notificationData.buttonLink,
            recipientIds: selectedUsers,
        };
        
        // Add meeting-specific fields if needed
        if (messageType === 'meeting') {
            notificationPayload.meetingLink = notificationData.buttonLink;
            notificationPayload.meetingTime = new Date().toISOString();
            notificationPayload.description = notificationData.content;
        }
        
        // Call Netlify Function with increased batch size
        const response = await fetch('/.netlify/functions/send-notification', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(notificationPayload)
        });
        
        if (!response.ok) {
            throw new Error(`HTTP ${response.status}`);
        }
        
        const result = await response.json();
        
        if (result.success) {
            showSnackbar(
                `✅ Email sent successfully! 📧 ${result.stats.emailsSent} emails delivered to ${result.stats.totalUsers} recipients`,
                'success'
            );
            
            // Reset form after successful send
            if (onSuccess) onSuccess();
            
            // Clear form
            setNotificationData({
                type: 'general_announcement',
                title: '',
                content: '',
                buttonText: '',
                buttonLink: '',
                recipients: []
            });
        } else {
            throw new Error(result.error || 'Failed to send emails');
        }
        
    } catch (error) {
        console.error('Error sending emails:', error);
        showSnackbar('Failed to send emails. Please try again.', 'error');
    } finally {
        setLoading(false);
    }
  };

  const showSnackbar = (message, severity) => {
    setSnackbar({
      open: true,
      message,
      severity
    });
  };

  const getRecipientCount = () => {
    return selectedUsers.length;
  };

  const getRecipientSummary = () => {
    if (selectedUsers.length === 0) return 'No recipients selected';
    if (selectedUsers.length === filteredUsers.length) {
      if (selectedYearGroup === 'all') {
        return `All ${selectedUsers.length} users`;
      } else {
        const group = yearGroups.get(selectedYearGroup);
        return `All ${selectedUsers.length} ${group?.displayName || 'members'}`;
      }
    }
    return `${selectedUsers.length} of ${filteredUsers.length} selected`;
  };

  // Get statistics for display
  const getGroupStats = () => {
    const stats = [];
    for (const [prefix, group] of yearGroups.entries()) {
      stats.push({
        prefix,
        displayName: group.displayName,
        count: group.count
      });
    }
    return stats.sort((a, b) => a.displayName.localeCompare(b.displayName));
  };

  // Count users with valid emails
  const getValidEmailCount = () => {
    const selectedUsersDetails = users.filter(user => selectedUsers.includes(user.id));
    return selectedUsersDetails.filter(user => user.email && user.email.trim()).length;
  };

  return (
    <Box sx={{ p: 3, maxWidth: 1200, mx: 'auto' }}>
      <Typography variant="h4" gutterBottom sx={{ color: '#1034A6', fontWeight: 'bold', mb: 3 }}>
        <EmailIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
        Mass Communication (Email Only)
      </Typography>
      
      <Grid container spacing={3}>
        {/* Left Column - Message Composition */}
        <Grid item xs={12} md={7}>
          <Card elevation={3}>
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <SendIcon /> Compose Email
              </Typography>
              <Divider sx={{ mb: 2 }} />
              
              <Stack spacing={3}>
                {/* Message Type Selection */}
                <Box>
                  <Typography variant="subtitle2" gutterBottom>Message Type</Typography>
                  <Stack direction="row" spacing={2}>
                    <Button
                      variant={messageType === 'general' ? 'contained' : 'outlined'}
                      onClick={() => handleMessageTypeChange('general')}
                      startIcon={<EmailIcon />}
                      sx={{ flex: 1 }}
                    >
                      General Announcement
                    </Button>
                    <Button
                      variant={messageType === 'meeting' ? 'contained' : 'outlined'}
                      onClick={() => handleMessageTypeChange('meeting')}
                      startIcon={<LinkIcon />}
                      sx={{ flex: 1 }}
                    >
                      Meeting Invitation
                    </Button>
                  </Stack>
                </Box>
                
                {/* Title */}
                <TextField
                  fullWidth
                  label="Email Subject"
                  value={notificationData.title}
                  onChange={(e) => handleInputChange('title', e.target.value)}
                  placeholder={messageType === 'meeting' ? "e.g., Weekly Prayer Meeting" : "e.g., Important Announcement"}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <TitleIcon color="primary" />
                      </InputAdornment>
                    ),
                  }}
                />
                
                {/* Content */}
                <TextField
                  fullWidth
                  multiline
                  rows={6}
                  label="Email Content"
                  value={notificationData.content}
                  onChange={(e) => handleInputChange('content', e.target.value)}
                  placeholder="Type your email message here..."
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <DescriptionIcon color="primary" />
                      </InputAdornment>
                    ),
                  }}
                />
                
                {/* Action Button */}
                <Box>
                  <Typography variant="subtitle2" gutterBottom>
                    Action Button {messageType === 'general' ? '(Optional)' : '(Required)'}
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={4}>
                      <TextField
                        fullWidth
                        label="Button Label"
                        value={notificationData.buttonText}
                        onChange={(e) => handleInputChange('buttonText', e.target.value)}
                        placeholder="e.g., Learn More, Join Now"
                      />
                    </Grid>
                    <Grid item xs={12} sm={8}>
                      <TextField
                        fullWidth
                        label="Button Link"
                        value={notificationData.buttonLink}
                        onChange={(e) => handleInputChange('buttonLink', e.target.value)}
                        placeholder="https://..."
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <LinkIcon />
                            </InputAdornment>
                          ),
                        }}
                      />
                    </Grid>
                  </Grid>
                </Box>
                
                {/* Preview Section */}
                {(notificationData.title || notificationData.content) && (
                  <Paper variant="outlined" sx={{ p: 2, bgcolor: '#f5f5f5' }}>
                    <Typography variant="subtitle2" gutterBottom>Email Preview</Typography>
                    <Box sx={{ p: 2, bgcolor: 'white', borderRadius: 1 }}>
                      <Typography variant="h6" sx={{ color: '#1034A6' }}>
                        {notificationData.title || 'Subject Preview'}
                      </Typography>
                      <Typography variant="body2" sx={{ mt: 1, whiteSpace: 'pre-wrap' }}>
                        {notificationData.content || 'Email content preview will appear here...'}
                      </Typography>
                      {notificationData.buttonText && notificationData.buttonLink && (
                        <Button
                          variant="contained"
                          size="small"
                          sx={{ mt: 2, bgcolor: '#1034A6' }}
                          disabled
                        >
                          {notificationData.buttonText}
                        </Button>
                      )}
                    </Box>
                  </Paper>
                )}
              </Stack>
            </CardContent>
          </Card>
        </Grid>
        
        {/* Right Column - Recipient Selection */}
        <Grid item xs={12} md={5}>
          <Card elevation={3}>
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <GroupIcon /> Select Recipients
              </Typography>
              <Divider sx={{ mb: 2 }} />
              
              <Stack spacing={2}>
                {/* Year Group Statistics Summary */}
                <Paper variant="outlined" sx={{ p: 1.5, bgcolor: '#f5f5f5' }}>
                  <Typography variant="subtitle2" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <PeopleIcon fontSize="small" /> Year Groups
                  </Typography>
                  <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                    <Chip 
                      label={`Total: ${users.length}`} 
                      size="small" 
                      color="primary" 
                      variant="outlined"
                    />
                    <Chip 
                      label={`With Email: ${users.filter(u => u.email).length}`} 
                      size="small" 
                      color="success" 
                      variant="outlined"
                    />
                    {getGroupStats().map((group) => (
                      <Chip 
                        key={group.prefix}
                        label={`${group.displayName}: ${group.count}`} 
                        size="small" 
                        variant="outlined"
                      />
                    ))}
                  </Stack>
                </Paper>
                
                {/* Filter by Year Group */}
                <FormControl fullWidth>
                  <InputLabel>Filter by Year Group</InputLabel>
                  <Select
                    value={selectedYearGroup}
                    label="Filter by Year Group"
                    onChange={handleYearGroupChange}
                  >
                    <MenuItem value="all">
                      <em>All Year Groups ({users.length} users)</em>
                    </MenuItem>
                    {getGroupStats().map((group) => (
                      <MenuItem key={group.prefix} value={group.prefix}>
                        {group.displayName} ({group.count} users)
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                
                {/* Recipient Summary */}
                <Paper variant="outlined" sx={{ p: 2, bgcolor: '#e3f2fd' }}>
                  <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Box>
                      <Typography variant="body2" color="textSecondary">
                        Selected Recipients
                      </Typography>
                      <Typography variant="h5" fontWeight="bold">
                        {getRecipientCount()}
                      </Typography>
                      <Typography variant="caption" color="textSecondary">
                        {getRecipientSummary()}
                      </Typography>
                      <Typography variant="caption" display="block" color="success.main">
                        {getValidEmailCount()} have valid email addresses
                      </Typography>
                    </Box>
                    <Button
                      variant="outlined"
                      onClick={handleSelectAll}
                      size="small"
                    >
                      {selectedUsers.length === filteredUsers.length ? 'Deselect All' : 'Select All'}
                    </Button>
                  </Stack>
                </Paper>
                
                {/* Users List */}
                <Box sx={{ maxHeight: 400, overflow: 'auto' }}>
                  <Typography variant="subtitle2" gutterBottom>
                    Users ({filteredUsers.length})
                  </Typography>
                  <Stack spacing={1}>
                    {filteredUsers.map((user) => (
                      <Paper
                        key={user.id}
                        variant="outlined"
                        sx={{
                          p: 1,
                          cursor: 'pointer',
                          bgcolor: selectedUsers.includes(user.id) ? '#e3f2fd' : 'white',
                          '&:hover': { bgcolor: '#f5f5f5' }
                        }}
                        onClick={() => handleUserToggle(user.id)}
                      >
                        <Stack direction="row" justifyContent="space-between" alignItems="center">
                          <Box>
                            <Typography variant="body2" fontWeight="bold">
                              {user.fullName || user.firstName || user.email}
                            </Typography>
                            <Typography variant="caption" color="textSecondary">
                              {user.email ? (
                                <><EmailIcon fontSize="inherit" sx={{ fontSize: 12, mr: 0.5 }} /> {user.email}</>
                              ) : (
                                <span style={{ color: '#f44336' }}>No email address</span>
                              )}
                              {' • '}
                              {user.yearName || 'Year not set'}
                            </Typography>
                          </Box>
                          {selectedUsers.includes(user.id) && (
                            <CheckCircleIcon color="primary" fontSize="small" />
                          )}
                        </Stack>
                      </Paper>
                    ))}
                    {filteredUsers.length === 0 && (
                      <Typography variant="body2" color="textSecondary" sx={{ textAlign: 'center', py: 4 }}>
                        No users found in this year group
                      </Typography>
                    )}
                  </Stack>
                </Box>
                
                {/* Send Button */}
                <Button
                  fullWidth
                  variant="contained"
                  size="large"
                  onClick={sendNotifications}
                  disabled={loading || selectedUsers.length === 0 || getValidEmailCount() === 0}
                  sx={{
                    mt: 2,
                    bgcolor: '#1034A6',
                    '&:hover': { bgcolor: '#0d2a8a' }
                  }}
                  startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <SendIcon />}
                >
                  {loading ? 'Sending Emails...' : `Send Email to ${getValidEmailCount()} Recipient${getValidEmailCount() !== 1 ? 's' : ''}`}
                </Button>
              </Stack>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
      
      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Communication;
